import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  PieChart, RefreshCw, Loader2, Shield, Receipt, Banknote, FileText,
  ChevronRight, Clock, AlertCircle,
} from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import HelpSectionHint from '../../components/portal/HelpSectionHint';

const API = API_BASE;

const formatMoney = (amount, currency = 'GBP') => {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(amount));
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey) return '—';
  const [year, month] = monthKey.split('-');
  const d = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(d.getTime())) return monthKey;
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

const currentMonthInput = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
};

export default function AccountsSummaryPage() {
  const { token, isAdmin } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [month, setMonth] = useState(currentMonthInput());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState('');
  const [statement, setStatement] = useState(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [allocationLines, setAllocationLines] = useState([{ invoice_reference: '', amount: '' }]);
  const [disputeInvoiceId, setDisputeInvoiceId] = useState('');
  const [disputeCategory, setDisputeCategory] = useState('hours_mismatch');
  const [disputeExplanation, setDisputeExplanation] = useState('');
  const [disputeAmount, setDisputeAmount] = useState('');
  const [disputeEvidenceNeeded, setDisputeEvidenceNeeded] = useState('');
  const [disputeCreditRequired, setDisputeCreditRequired] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!isAdmin()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (month) params.set('month', month);
      const res = await axios.get(`${API}/accounts/summary?${params}`, { headers });
      setSummary(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Accounts is admin-only');
      } else {
        toast.error('Failed to load accounts summary');
      }
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [headers, month, isAdmin]);

  const fetchOrganisations = useCallback(async () => {
    if (!isAdmin()) return;
    try {
      const res = await axios.get(`${API}/organisations`, { headers });
      const rows = (res.data?.organisations || []).filter((row) => row.is_active !== false);
      setOrganisations(rows);
      setSelectedOrganisationId((current) => current || rows[0]?.id || '');
    } catch {
      setOrganisations([]);
    }
  }, [headers, isAdmin]);

  const fetchStatement = useCallback(async () => {
    if (!isAdmin() || !selectedOrganisationId) {
      setStatement(null);
      return;
    }
    setReconciliationLoading(true);
    try {
      const res = await axios.get(
        `${API}/accounts/organisations/${selectedOrganisationId}/statement`,
        { headers },
      );
      setStatement(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to load client statement');
      setStatement(null);
    } finally {
      setReconciliationLoading(false);
    }
  }, [headers, isAdmin, selectedOrganisationId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchOrganisations();
  }, [fetchOrganisations]);

  useEffect(() => {
    fetchStatement();
  }, [fetchStatement]);

  const refreshReconciliation = async () => {
    await Promise.all([fetchSummary(), fetchStatement()]);
  };

  const updateAllocationLine = (index, key, value) => {
    setAllocationLines((current) => current.map((line, i) => (
      i === index ? { ...line, [key]: value } : line
    )));
  };

  const addAllocationLine = () => {
    setAllocationLines((current) => [...current, { invoice_reference: '', amount: '' }]);
  };

  const removeAllocationLine = (index) => {
    setAllocationLines((current) => {
      const next = current.filter((_, i) => i !== index);
      return next.length ? next : [{ invoice_reference: '', amount: '' }];
    });
  };

  const handleRecordRemittance = async () => {
    const amount = Number(paymentAmount);
    if (!selectedOrganisationId || !paymentDate || !paymentReference.trim() || !(amount > 0)) {
      toast.error('Choose a client and enter the payment date, amount and bank/remittance reference');
      return;
    }
    const allocations = allocationLines
      .map((line) => ({
        invoice_reference: line.invoice_reference.trim(),
        amount: Number(line.amount),
      }))
      .filter((line) => line.invoice_reference && line.amount > 0);

    setReconciliationLoading(true);
    try {
      const res = await axios.post(
        `${API}/accounts/remittances`,
        {
          organisation_id: selectedOrganisationId,
          payment_date: paymentDate,
          amount,
          reference: paymentReference.trim(),
          notes: paymentNotes.trim() || undefined,
          allocations,
        },
        { headers },
      );
      const unapplied = Number(res.data?.unapplied_amount || 0);
      toast.success(
        unapplied > 0
          ? `Payment recorded · ${formatMoney(unapplied)} still unapplied`
          : 'Payment recorded and reconciled',
      );
      setPaymentAmount('');
      setPaymentReference('');
      setPaymentNotes('');
      setAllocationLines([{ invoice_reference: '', amount: '' }]);
      await refreshReconciliation();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not record remittance');
    } finally {
      setReconciliationLoading(false);
    }
  };

  const handleRecordDispute = async () => {
    if (!disputeInvoiceId || disputeExplanation.trim().length < 3) {
      toast.error('Choose an invoice and describe the client dispute');
      return;
    }
    setReconciliationLoading(true);
    try {
      await axios.post(
        `${API}/accounts/invoices/${disputeInvoiceId}/disputes`,
        {
          category: disputeCategory,
          explanation: disputeExplanation.trim(),
          amount_disputed: Number(disputeAmount || 0),
          evidence_needed: disputeEvidenceNeeded.trim() || undefined,
          credit_note_required: disputeCreditRequired,
        },
        { headers },
      );
      toast.success('Invoice dispute recorded');
      setDisputeInvoiceId('');
      setDisputeExplanation('');
      setDisputeAmount('');
      setDisputeEvidenceNeeded('');
      setDisputeCreditRequired(false);
      await refreshReconciliation();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not record dispute');
    } finally {
      setReconciliationLoading(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center space-y-4">
        <Shield className="h-12 w-12 text-text-muted mx-auto" />
        <h1 className="text-xl font-heading font-semibold">Accounts — admin only</h1>
        <p className="text-sm text-text-muted">
          Financial summaries are restricted to admin accounts.
        </p>
      </div>
    );
  }

  const awaiting = summary?.awaiting_timesheet || {};
  const locked = summary?.locked_timesheets_ready || {};
  const payroll = summary?.payroll_batches || {};
  const invoices = summary?.invoices || {};
  const receivables = summary?.receivables || {};
  const history = summary?.monthly_history || [];

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-text-primary flex items-center gap-2">
            <PieChart className="h-7 w-7 text-primary" />
            Accounts
            <HelpSectionHint sectionId="awaiting" />
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Month view across the predictable financial pipeline: awaiting timesheet → locked (ready for payroll) → payroll batches + client invoices. "Financially Closed" = payroll_processed + invoice_processed + fully_processed on the attendance records (visible in Shifts archive).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchSummary} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="space-y-2">
            <Label htmlFor="accounts-month">Month</Label>
            <Input
              id="accounts-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-48"
            />
          </div>
          <p className="text-sm text-text-muted pb-2">
            Showing {formatMonthLabel(summary?.month || month)}
          </p>
        </CardContent>
      </Card>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-text-muted">
        Predictable pipeline: <span className="font-medium text-text-primary">Shifts (approve attendance with billing client + standard role)</span> → <span className="font-medium text-text-primary">Timesheets (generate + lock)</span> → parallel <span className="font-medium text-text-primary">Payroll (worker pay from locked timesheets)</span> + <span className="font-medium text-text-primary">Invoices (client billing from charge rates)</span> → <span className="font-medium text-emerald-700">Financially Closed</span> (badges appear in Shifts processed/archive and records are fully_processed). Role dropdowns in Organisations/Shifts/Requests prevent the #1 mismatch that breaks invoicing.
      </div>

      {loading && !summary ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Card data-help-section="awaiting">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-text-muted flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Awaiting timesheet
                  <HelpSectionHint sectionId="awaiting" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-text-primary">{awaiting.count ?? 0}</p>
                <p className="text-xs text-text-muted mt-1">
                  {(awaiting.hours ?? 0).toFixed(2)} payable hours this month — next step in pipeline after Shifts approval.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-text-muted flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  Locked — ready for payroll
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-text-primary">{locked.count ?? 0}</p>
                <p className="text-xs text-text-muted mt-1">
                  {(locked.hours ?? 0).toFixed(2)}h · est. {formatMoney(locked.estimated_gross)} — locked timesheets feed both payroll and (parallel) client invoices.
                </p>
                {locked.missing_pay_rates > 0 ? (
                  <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {locked.missing_pay_rates} missing pay rate(s)
                  </p>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-text-muted flex items-center gap-2">
                  <Banknote className="h-4 w-4" />
                  Payroll batches
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-text-primary">{payroll.count ?? 0}</p>
                <p className="text-xs text-text-muted mt-1">
                  {formatMoney(payroll.total_gross_pay)} gross this month — worker pay side. Combine with client invoices below to reach Financially Closed.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-text-muted flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Client invoices
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-text-primary">
                  {formatMoney(invoices.outstanding)}
                </p>
                <p className="text-xs text-text-muted mt-1">
                  outstanding · {formatMoney(invoices.total_invoiced)} invoiced ·{' '}
                  {formatMoney(invoices.total_paid)} paid — billing side. When payroll locked + invoice sent/paid for same periods → Financially Closed.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="pt-5">
                <p className="text-xs text-text-muted">Client payments reconciled</p>
                <p className="text-xl font-semibold text-emerald-700">{formatMoney(receivables.paid)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <p className="text-xs text-text-muted">Outstanding undisputed</p>
                <p className="text-xl font-semibold">{formatMoney(receivables.outstanding_undisputed)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <p className="text-xs text-text-muted">Held in dispute</p>
                <p className="text-xl font-semibold text-amber-700">{formatMoney(receivables.disputed)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <p className="text-xs text-text-muted">Overdue</p>
                <p className="text-xl font-semibold text-red-700">{formatMoney(receivables.overdue)}</p>
                {(receivables.unapplied_remittances || 0) > 0 && (
                  <p className="text-[11px] text-blue-700 mt-1">
                    {formatMoney(receivables.unapplied_remittances)} received but not yet allocated
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Payroll batches this month</CardTitle>
              </CardHeader>
              <CardContent>
                {(payroll.batches || []).length === 0 ? (
                  <p className="text-sm text-text-muted">No payroll batches created this month. They appear here after generation+lock on the Payroll page.</p>
                ) : (
                  <div className="space-y-2">
                    {payroll.batches.map((batch) => (
                      <div
                        key={batch.id}
                        className="flex items-center justify-between rounded-lg border border-[#E4E8EB] px-3 py-2 text-sm"
                      >
                        <div>
                          <p className="font-medium">{batch.reference}</p>
                          <p className="text-xs text-text-muted capitalize">{batch.status}</p>
                        </div>
                        <p className="font-medium">{formatMoney(batch.total_gross_pay)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Invoiced vs paid (last 3 months)</CardTitle>
              </CardHeader>
              <p className="px-6 -mt-3 pb-1 text-[11px] text-text-muted">When payroll batches are locked AND invoices reach sent/paid for matching periods, the source attendance records become Financially Closed (full pipeline end state).</p>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-text-muted">
                        <th className="py-2 pr-4 font-medium">Month</th>
                        <th className="py-2 pr-4 font-medium text-right">Invoiced</th>
                        <th className="py-2 font-medium text-right">Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((row) => (
                        <tr key={row.month} className="border-b last:border-0">
                          <td className="py-2 pr-4">{formatMonthLabel(row.month)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(row.invoiced)}</td>
                          <td className="py-2 text-right">{formatMoney(row.paid)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Client reconciliation</CardTitle>
              <p className="text-sm text-text-muted">
                Record bank/remittance payments once, allocate them to one or more invoices, and see the live statement for each care home.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="reconciliation-client">Care home / client</Label>
                  <select
                    id="reconciliation-client"
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={selectedOrganisationId}
                    onChange={(e) => setSelectedOrganisationId(e.target.value)}
                  >
                    <option value="">Select client</option>
                    {organisations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <div className="rounded-lg border bg-slate-50 p-3">
                  <p className="text-xs text-text-muted">Statement outstanding</p>
                  <p className="text-xl font-semibold">{formatMoney(statement?.totals?.outstanding)}</p>
                </div>
                <div className="rounded-lg border bg-slate-50 p-3">
                  <p className="text-xs text-text-muted">Unapplied payments</p>
                  <p className="text-xl font-semibold text-blue-700">{formatMoney(statement?.totals?.unapplied_remittances)}</p>
                </div>
              </div>

              <div className="grid gap-6 xl:grid-cols-2">
                <div className="rounded-lg border p-4 space-y-4">
                  <div>
                    <p className="font-medium">Record client payment / remittance</p>
                    <p className="text-xs text-text-muted mt-1">
                      Use the bank/BACS reference. Add invoice allocations below; leaving them blank records the payment as unapplied unless the bank reference exactly matches an invoice number.
                    </p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="space-y-1">
                      <Label htmlFor="payment-date">Payment date</Label>
                      <Input id="payment-date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="payment-amount">Amount</Label>
                      <Input id="payment-amount" type="number" min="0" step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder="0.00" />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="payment-reference">BACS / remittance ref</Label>
                      <Input id="payment-reference" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} placeholder="e.g. BACS-123" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="payment-notes">Notes (optional)</Label>
                    <Input id="payment-notes" value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} placeholder="Client remittance advice, payer note, etc." />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium">Invoice allocations</p>
                      <Button type="button" variant="outline" size="sm" onClick={addAllocationLine}>Add invoice</Button>
                    </div>
                    {allocationLines.map((line, index) => (
                      <div key={index} className="grid gap-2 sm:grid-cols-[1fr_120px_auto]">
                        <Input
                          aria-label={`Invoice reference allocation ${index + 1}`}
                          value={line.invoice_reference}
                          onChange={(e) => updateAllocationLine(index, 'invoice_reference', e.target.value)}
                          placeholder="INV-2026-..."
                        />
                        <Input
                          aria-label={`Allocation amount ${index + 1}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.amount}
                          onChange={(e) => updateAllocationLine(index, 'amount', e.target.value)}
                          placeholder="0.00"
                        />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeAllocationLine(index)}>
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button onClick={handleRecordRemittance} disabled={reconciliationLoading || !selectedOrganisationId}>
                    {reconciliationLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Banknote className="h-4 w-4 mr-2" />}
                    Record payment
                  </Button>
                </div>

                <div className="rounded-lg border p-4 space-y-4">
                  <div>
                    <p className="font-medium">Record a client invoice dispute</p>
                    <p className="text-xs text-text-muted mt-1">
                      Use this when a care home challenges hours, worker, location, duplicate billing, missing evidence or another invoice issue.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="dispute-invoice">Invoice</Label>
                    <select
                      id="dispute-invoice"
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                      value={disputeInvoiceId}
                      onChange={(e) => setDisputeInvoiceId(e.target.value)}
                    >
                      <option value="">Select invoice</option>
                      {(statement?.invoices || []).filter((row) => row.outstanding > 0).map((row) => (
                        <option key={row.invoice_id} value={row.invoice_id}>
                          {row.reference} · {formatMoney(row.outstanding)} outstanding
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="dispute-category">Category</Label>
                      <select
                        id="dispute-category"
                        className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                        value={disputeCategory}
                        onChange={(e) => setDisputeCategory(e.target.value)}
                      >
                        <option value="wrong_care_home">Wrong care home</option>
                        <option value="wrong_worker">Wrong worker</option>
                        <option value="duplicate_invoice">Duplicate invoice</option>
                        <option value="duplicate_shift">Duplicate shift</option>
                        <option value="hours_mismatch">Hours mismatch</option>
                        <option value="late_arrival">Late arrival</option>
                        <option value="break_discrepancy">Break discrepancy</option>
                        <option value="missing_timesheet_evidence">Missing timesheet / evidence</option>
                        <option value="unsigned_unconfirmed_attendance">Unsigned / unconfirmed attendance</option>
                        <option value="already_paid">Already paid</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="dispute-amount">Amount disputed</Label>
                      <Input id="dispute-amount" type="number" min="0" step="0.01" value={disputeAmount} onChange={(e) => setDisputeAmount(e.target.value)} placeholder="0.00" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="dispute-explanation">Client explanation</Label>
                    <Input id="dispute-explanation" value={disputeExplanation} onChange={(e) => setDisputeExplanation(e.target.value)} placeholder="What did the client challenge?" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="dispute-evidence">Evidence needed (optional)</Label>
                    <Input id="dispute-evidence" value={disputeEvidenceNeeded} onChange={(e) => setDisputeEvidenceNeeded(e.target.value)} placeholder="e.g. approved timesheet, clock evidence" />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={disputeCreditRequired}
                      onChange={(e) => setDisputeCreditRequired(e.target.checked)}
                    />
                    Client issue may require a credit note
                  </label>
                  <Button variant="outline" onClick={handleRecordDispute} disabled={reconciliationLoading || !disputeInvoiceId}>
                    Record dispute
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium">Live client statement</p>
                    <p className="text-xs text-text-muted">
                      Exactly what is paid, credited, disputed, overdue and still outstanding.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchStatement} disabled={reconciliationLoading || !selectedOrganisationId}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${reconciliationLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-t border-b text-left text-text-muted">
                        <th className="px-3 py-2">Invoice</th>
                        <th className="px-3 py-2 text-right">Original</th>
                        <th className="px-3 py-2 text-right">Credits</th>
                        <th className="px-3 py-2 text-right">Paid</th>
                        <th className="px-3 py-2 text-right">Outstanding</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Next action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(statement?.invoices || []).length === 0 ? (
                        <tr><td colSpan={7} className="px-3 py-6 text-center text-text-muted">No issued invoices for this client yet.</td></tr>
                      ) : (statement?.invoices || []).map((row) => (
                        <tr key={row.invoice_id} className="border-b last:border-0">
                          <td className="px-3 py-2 font-medium">{row.reference}</td>
                          <td className="px-3 py-2 text-right">{formatMoney(row.original_total)}</td>
                          <td className="px-3 py-2 text-right">{formatMoney(row.credits)}</td>
                          <td className="px-3 py-2 text-right">{formatMoney(row.payments)}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatMoney(row.outstanding)}</td>
                          <td className="px-3 py-2 capitalize">{String(row.state || '').replaceAll('_', ' ')}</td>
                          <td className="px-3 py-2 text-xs">{row.next_action}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {statement?.totals && (
                  <div className="px-4 py-3 bg-slate-50 text-xs text-text-muted flex flex-wrap gap-x-5 gap-y-1">
                    <span>Original {formatMoney(statement.totals.original_total)}</span>
                    <span>Credits {formatMoney(statement.totals.credits)}</span>
                    <span>Payments {formatMoney(statement.totals.payments)}</span>
                    <span className="font-medium text-text-primary">Outstanding {formatMoney(statement.totals.outstanding)}</span>
                    <span>Disputed {formatMoney(statement.totals.disputed)}</span>
                    <span>Overdue {formatMoney(statement.totals.overdue)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick links — the full predictable pipeline</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              {[
                { href: '/portal/timesheets', label: 'Timesheets', icon: Receipt },
                { href: '/portal/payroll', label: 'Payroll', icon: Banknote },
                { href: '/portal/invoices', label: 'Invoices', icon: FileText },
                { href: '/portal/shifts', label: 'Shifts (see Financially Closed)', icon: Receipt },
              ].map(({ href, label, icon: Icon }) => (
                <Link key={href} to={href}>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Icon className="h-4 w-4" />
                    {label}
                    <ChevronRight className="h-4 w-4 opacity-50" />
                  </Button>
                </Link>
              ))}
              <p className="text-[11px] text-text-muted w-full mt-1">Use Shifts to approve attendance (with billing client) and to see the final "Financially Closed" badges once both sides complete.</p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}